import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { scheduleApi } from '../../api/endpoints.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import { formatMinutes } from '../../i18n/format.ts';
import { formatShortInstant } from '../../i18n/datetime.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import { useSession } from '../../state/session.tsx';
import { usePlanFeature } from '../plan-gate.tsx';
import { Alert, Button } from '../ui.tsx';
import type { VisitProfitSummary } from '@shared/types/index.ts';

/** "42.3%" from basis points; one decimal, like the reports. */
export function formatMargin(basisPoints: number): string {
  return `${(basisPoints / 100).toFixed(1)}%`;
}

/**
 * Green at or above the target, amber below it, red when the job lost money.
 * Without a target (Starter, or none set), anything that made money is green.
 */
export function marginTone(
  summary: Pick<VisitProfitSummary, 'profitCents' | 'marginBasisPoints' | 'targetMarginBasisPoints'>,
) {
  if (summary.profitCents < 0) return 'text-[color:var(--color-danger)]';
  if (
    summary.targetMarginBasisPoints !== null &&
    summary.marginBasisPoints !== null &&
    summary.marginBasisPoints < summary.targetMarginBasisPoints
  ) {
    return 'text-[color:var(--color-warning)]';
  }
  return 'text-[color:var(--color-brand)]';
}

/**
 * The Job profit card on a visit (migration 0035): what a completed visit
 * made after labor (with the W-2 payroll load), card fees and supplies, or
 * what an open one is expected to make. Owner and managers only — the route
 * refuses everybody else, and the panel does not ask on their behalf.
 */
export function JobProfitCard({ appointmentId }: { appointmentId: string }) {
  const { t } = useTranslation();
  const { company, user } = useSession();
  const insights = usePlanFeature('profit_insights');
  const query = useQuery({
    queryKey: ['appointment-profit', appointmentId],
    queryFn: () => scheduleApi.profit(appointmentId),
  });
  const profit = query.data?.profit ?? null;
  if (query.isError) {
    // A failed read is said, with a way to ask again — never mistaken for a
    // visit that has no profit to show.
    return (
      <section className="mt-4 border-t border-[color:var(--color-line)] pt-3">
        <p className="mb-2 text-sm font-semibold">{t('jobProfit.card.heading')}</p>
        <Alert tone="error">{t('jobProfit.card.loadError')}</Alert>
        <Button className="mt-2" busy={query.isFetching} onClick={() => void query.refetch()}>
          {t('jobProfit.card.retry')}
        </Button>
      </section>
    );
  }
  if (query.isLoading || !profit) return null;

  const currency = profit.currencyCode || company?.currencyCode || 'USD';
  const money = (cents: number) => formatCents(cents, currency, getDateLocale());
  const missing = profit.lines.filter((line) => line.status === 'pay_missing').map((line) => line.name);
  const isOwner = user?.role === 'owner';
  // Somebody's pay, or the whole crew, is missing: the labor total covers
  // only part of the work (or none of it), so neither it nor the profit is
  // stated as a figure.
  const laborUnknown = profit.payMissing || profit.crewMissing;
  const belowTarget =
    profit.targetMarginBasisPoints !== null &&
    profit.marginBasisPoints !== null &&
    profit.marginBasisPoints < profit.targetMarginBasisPoints;

  const row = (
    label: string,
    cents: number,
    options: { negative?: boolean; strong?: boolean; indent?: boolean } = {},
  ) => (
    <div className={`flex justify-between gap-3 ${options.indent ? 'pl-4 text-[color:var(--color-muted)]' : ''}`}>
      <dt className={options.strong ? 'font-semibold' : undefined}>{label}</dt>
      <dd className={`tabular ${options.strong ? 'font-semibold' : ''}`}>
        {options.negative && cents > 0 ? `−${money(cents)}` : money(cents)}
      </dd>
    </div>
  );

  return (
    <section
      className="mt-4 border-t border-[color:var(--color-line)] pt-3"
      aria-labelledby={`job-profit-${appointmentId}`}
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p id={`job-profit-${appointmentId}`} className="text-sm font-semibold">
          {profit.kind === 'estimate' ? t('jobProfit.card.estimatedHeading') : t('jobProfit.card.heading')}
        </p>
        <div className="flex flex-wrap gap-2">
          {profit.kind === 'estimate' ? (
            <span className="badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
              {t('jobProfit.card.estimateBadge')}
            </span>
          ) : null}
          {profit.timeIncomplete ? (
            <span className="badge border-[color:var(--color-warning)] text-[color:var(--color-warning)]">
              {t('jobProfit.card.timeIncomplete')}
            </span>
          ) : null}
          {profit.updatedAt ? (
            <span className="badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
              {t('jobProfit.card.updated', { when: formatShortInstant(profit.updatedAt) })}
            </span>
          ) : null}
        </div>
      </div>

      {missing.length > 0 || (profit.payMissing && profit.kind === 'estimate') ? (
        <p className="mb-2 text-sm">
          {missing.length > 0
            ? t('jobProfit.card.payMissing', { names: missing.join(', ') })
            : t('jobProfit.card.payMissingTeam')}{' '}
          {isOwner ? (
            <Link to="/settings#settings-teamPay" className="font-semibold text-[color:var(--color-brand)] underline">
              {t('jobProfit.card.setPay')}
            </Link>
          ) : null}
        </p>
      ) : null}

      <dl className="space-y-1 text-sm">
        {row(
          profit.lateCancelFee ? t('jobProfit.card.lateFeeRevenue') : t('jobProfit.card.revenue'),
          profit.revenueCents,
        )}
        {laborUnknown ? (
          <div className="flex justify-between gap-3">
            <dt>{t('jobProfit.card.labor')}</dt>
            <dd className="text-[color:var(--color-muted)]">{t('jobProfit.card.profitUnknown')}</dd>
          </div>
        ) : (
          row(t('jobProfit.card.labor'), profit.laborCents, { negative: true })
        )}
        {!laborUnknown && profit.burdenCents > 0
          ? row(t('jobProfit.card.burden'), profit.burdenCents, { negative: true, indent: true })
          : null}
        {row(t('jobProfit.card.fees'), profit.feeCents, { negative: true })}
        {profit.suppliesCents > 0 ? row(t('jobProfit.card.supplies'), profit.suppliesCents, { negative: true }) : null}
        {laborUnknown ? (
          <div className="flex justify-between gap-3">
            <dt className="font-semibold">{t('jobProfit.card.profit')}</dt>
            <dd className="text-[color:var(--color-muted)]">{t('jobProfit.card.profitUnknown')}</dd>
          </div>
        ) : (
          <div className="flex justify-between gap-3 border-t border-[color:var(--color-line)] pt-1">
            <dt className="font-semibold">{t('jobProfit.card.profit')}</dt>
            <dd className={`tabular font-semibold ${marginTone(profit)}`}>
              {money(profit.profitCents)}
              {profit.marginBasisPoints !== null ? ` · ${formatMargin(profit.marginBasisPoints)}` : ''}
            </dd>
          </div>
        )}
      </dl>

      {profit.targetMarginBasisPoints !== null &&
      !profit.payMissing &&
      !profit.crewMissing &&
      profit.marginBasisPoints !== null ? (
        // Below the target is said in words as well as color.
        <p className={belowTarget ? 'mt-1 text-sm font-semibold text-[color:var(--color-warning)]' : 'field-hint mt-1'}>
          {t(belowTarget ? 'jobProfit.card.belowTarget' : 'jobProfit.card.target', {
            target: formatMargin(profit.targetMarginBasisPoints),
          })}
        </p>
      ) : null}

      {profit.kind === 'actual' && profit.estimatedLaborMinutes !== null ? (
        <p className="mt-2 text-sm text-[color:var(--color-muted)]">
          {t('jobProfit.card.hours', {
            actual: formatMinutes(profit.laborMinutes),
            estimated: formatMinutes(profit.estimatedLaborMinutes),
          })}
          {profit.estimatedMarginBasisPoints !== null && !profit.payMissing && !profit.crewMissing
            ? ` · ${t('jobProfit.card.expectedMargin', { margin: formatMargin(profit.estimatedMarginBasisPoints) })}`
            : ''}{' '}
          <Link
            to={
              insights
                ? company?.reportsHubRoutes
                  ? '/reports/jobProfit'
                  : '/reports?section=jobProfit'
                : '/dashboard'
            }
            className="font-semibold text-[color:var(--color-brand)] underline"
          >
            {insights ? t('jobProfit.card.openReport') : t('jobProfit.card.openDashboard')}
          </Link>
        </p>
      ) : null}

      {profit.lines.length > 0 ? (
        <details className="mt-2 text-sm">
          <summary className="cursor-pointer text-[color:var(--color-muted)]">{t('jobProfit.card.byPerson')}</summary>
          <ul className="mt-1 space-y-1">
            {profit.lines.map((line) => (
              <li key={line.userId} className="flex flex-wrap justify-between gap-2">
                <span>
                  {line.name}
                  <span className="text-[color:var(--color-muted)]">
                    {' · '}
                    {line.workerType ? t(`jobProfit.workerType.${line.workerType}`) : t('jobProfit.workerType.unset')}
                    {line.payType ? ` · ${t(`jobProfit.payType.${line.payType}`)}` : ''}
                    {' · '}
                    {formatMinutes(line.minutes + line.unpaidMinutes)}
                    {line.minutesSource !== 'clock'
                      ? ` (${t(`jobProfit.card.minutesSource.${line.minutesSource}`)})`
                      : ''}
                  </span>
                </span>
                <span className="tabular">
                  {line.status === 'costed'
                    ? money(line.baseCents + line.burdenCents)
                    : t(`jobProfit.card.lineStatus.${line.status}`)}
                </span>
              </li>
            ))}
          </ul>
          {profit.openSlots > 0 ? (
            <p className="field-hint mt-1">{t('jobProfit.card.openSlots', { count: profit.openSlots })}</p>
          ) : null}
        </details>
      ) : (
        <p className="field-hint mt-2">
          {profit.crewMissing
            ? t('jobProfit.card.crewMissing')
            : profit.kind === 'estimate'
              ? t('jobProfit.card.noCrewEstimate', { count: Math.max(1, profit.openSlots) })
              : t('jobProfit.card.noCrew')}
        </p>
      )}

      {profit.timeIncomplete ? <p className="field-hint mt-2">{t('jobProfit.card.timeIncompleteHint')}</p> : null}
      {profit.approximate ? <p className="field-hint mt-1">{t('jobProfit.card.approximate')}</p> : null}
      <p className="field-hint mt-1">{t('jobProfit.card.disclaimer')}</p>
    </section>
  );
}
